// Libraries
import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

// Types
import User from '@/types/user';
import Nullable from '@/types/nullable';
import IncomingPostMessage from '@/types/incoming-post-message';

// Utils
import sentryUtils from '@/utils/3rd-party/sentry';
import { executePostMessageStrategy } from '@/utils/post-messages/incoming-messages/strategy-factory';
import { PERMISSIONS, userHasPermission } from '@/utils/permissions';

interface PostMessageProps {
  user: Nullable<User>;
  isPostMessageActive: boolean;
}

export const PostMessage = (props: PostMessageProps) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const dispatchPostMessage = useCallback(
    (event: MessageEvent<unknown>) => {
      const data = event.data as IncomingPostMessage;
      if (props.user == null || props.user.appConfig.postMessages == null) {
        return;
      }

      // All events supported in CapIntel should have an action field, ignoring if it's not the case
      if (data.action == null) {
        return;
      }

      if (!props.user.appConfig.postMessages.actionsToListen.includes(data.action)) {
        sentryUtils.captureError(new Error(`[POST_MESSAGE] actionsToListen doesn't include ${data.action}`));
        return;
      }

      if (!props.user.appConfig.postMessages.sourceOrigins.includes(event.origin)) {
        sentryUtils.captureError(
          new Error(`[POST_MESSAGE] event origin != to configured origin: ${event.origin} != ${props.user.appConfig.postMessages.sourceOrigins}`),
        );
        return;
      }

      console.log(`Processing ${data.action} with context: ${JSON.stringify(data.context)}`);
      executePostMessageStrategy(dispatch, data, history);
    },
    [dispatch, history, props],
  );

  useEffect(() => {
    if (props.user && userHasPermission(props.user, PERMISSIONS.POST_MESSAGE) && props.isPostMessageActive) {
      window.addEventListener('message', dispatchPostMessage);

      return () => {
        window.removeEventListener('message', dispatchPostMessage);
      };
    }
  }, [dispatchPostMessage, props]);

  return null;
};
